import * as React from "react";
import {clsx} from "clsx";
import {formatTimeZone, parseTimeZone} from "@/shared/utils/datefunctions";
import {useDraftInput} from "@/shared/ui/primitives/useDraftInput";

interface TimeZoneInputProps {
	id?: string;
	name?: string;
	/** UTC offset as `±HH:mm`, or null when the field is empty or invalid. */
	value: string | null;
	/** Fired on blur, only when the normalized draft differs from {@link TimeZoneInputProps.value}. */
	onValueChange: (value: string | null) => void;
	className?: string;
	ariaLabel?: string;
	disabled?: boolean;
}

export function normalizeTimeZoneInputValue(value: string | null): string | null {
	const trimmedValue = value?.trim() ?? "";
	if (!trimmedValue || !globals.timeZonePattern.test(trimmedValue)) {
		return null;
	}
	const offsetMinutes = parseTimeZone(trimmedValue);
	return Number.isNaN(offsetMinutes) ? null : formatTimeZone(offsetMinutes);
}

export function isValidTimeZoneInputValue(value: string | null): boolean {
	return normalizeTimeZoneInputValue(value) !== null;
}

/**
 * Text-based UTC-offset input with local draft state and blur-time normalization; see
 * {@link useDraftInput} for the draft/commit semantics.
 */
export function TimeZoneInput({
	id,
	name,
	value,
	onValueChange,
	className,
	ariaLabel,
	disabled = false
}: Readonly<TimeZoneInputProps>) {
	const draftInputProps = useDraftInput({
		value,
		format: current => current ?? "",
		parse: normalizeTimeZoneInputValue,
		onValueChange
	});

	return (
		<input
			id={id}
			type="text"
			name={name}
			className={clsx("form-control", className)}
			aria-label={ariaLabel}
			maxLength={6}
			disabled={disabled}
			{...draftInputProps}
		/>
	);
}

export type {TimeZoneInputProps};
